import { customElement, property, state } from 'lit/decorators.js';
import { html, LitElement, nothing, PropertyValues } from 'lit';
import { PathRouteConfig, RouteConfig } from '@lit-labs/router';
import {
  BrandMutationEvent,
  CartPositions,
  Messages,
  Product,
  SizeDeterminedContainer,
  WishlistBrandListItem,
  WishlistCollection,
  WishlistMutationEvent,
} from '../../../@types/wishlist.js';
import '../remove-article-layer/remove-article-layer.js';
import '../share-collection-layer/share-collection-layer.js';
import '../remove-collection-layer/remove-collection-layer.js';
import '../curator-layer/curator-layer.js';
import '../wishlist-curated-collections/wishlist-curated-collections.js';
import '../wishlist-brands/wishlist-brands.js';
import '../wishlist-newsletter-teaser/wishlist-newsletter-teaser.js';
import { provide } from '@lit/context';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import {
  WishlistAppConfig,
  wishlistAppConfigContext,
  wishlistRouterContext,
} from '../../../scripts/context/appConfigContext.js';
import { WishlistContainer } from '../wishlist-container/wishlist-container.js';
import { productApi } from '../../../scripts/service/product-api.js';
import { cartApi } from '../../../scripts/service/cart-api.js';
import { WishlistRouter } from './wishlist-router.js';
import { collectionMenuContext, productMenuContext } from '../../../scripts/context/menuContext.js';
import {
  CollectionMenuState,
  CollectionMenuStateData,
  CollectionMenuStateImpl,
  EMPTY_COLLECTION_MENU_STATE_DATA,
  EMPTY_PRODUCT_MENU_STATE_DATA,
  ProductMenuState,
  ProductMenuStateData,
  ProductMenuStateImpl,
} from '../../../scripts/context/menu-state.js';
import {
  brandsContext,
  collectionsContext,
  curatedCollectionContext,
  productsContext,
} from '../../../scripts/context/wishlistContext.js';
import { cartContext } from '../../../scripts/context/cartContext.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import {
  BrandsState,
  BrandsStateImpl,
  CartState,
  CartStateImpl,
  CollectionsState,
  CollectionsStateImpl,
  CuratedCollectionsState,
  CuratedCollectionsStateImpl,
  ProductsState,
  ProductsStateImpl,
} from '../../../scripts/context/wishlist-state.js';
import { loadFromDOM } from '../../../scripts/utils/load-from-dom.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { curatorLayerContext, CuratorLayerState } from '../../../scripts/context/curatorLayerContext.js';
import { VtoService } from '../../../scripts/service/vto-service.js';
import { vtoContext } from '../../../scripts/context/vtoContext.js';
import { ADDED_BRAND_EVENT, REMOVED_BRAND_EVENT } from '../../../scripts/service/config.js';

@customElement('ents-wishlist-app')
export class AppMain extends LitElement {
  private static readonly FAVORITES_TAB = 'ents-wishlist-nav__item__favoriten';
  private static readonly COLLECTIONS_TAB = 'ents-wishlist-nav__item__collection';
  private static readonly CURATED_COLLECTION_TAB = 'ents-wishlist-nav__item__curated-collection';
  private static readonly LOOKS_TAB = 'ents-wishlist-nav__item__looks';
  private static readonly BRANDS_TAB = 'ents-wishlist-nav__item__brands';

  @property({ type: Boolean, attribute: 'logged-in' })
  private loggedIn: boolean = false;

  @provide({ context: collectionsContext })
  @property({ attribute: false })
  private collectionsState: CollectionsState = this.createCollectionsState(this, []);

  private createCollectionsState(outerThis: this, wishlistCollections: WishlistCollection[]): CollectionsState {
    return new (class extends CollectionsStateImpl {
      entities = wishlistCollections;
      updateAll = (newState: WishlistCollection[]): void => {
        outerThis.collectionsState = outerThis.createCollectionsState(outerThis, newState);
      };
    })();
  }

  @provide({ context: productsContext })
  @property({ attribute: false })
  private productsState: ProductsState = this.createProductState(this, []);

  private createProductState(outerThis: this, products: Product[], sharedProducts?: Product[]): ProductsState {
    return new (class extends ProductsStateImpl {
      sharedProducts = sharedProducts;
      entities = products;
      updateProductsAndSharedProducts = (newProducts: Product[], newShared?: Product[]): void => {
        outerThis.productsState = outerThis.createProductState(outerThis, newProducts, newShared);
      };
    })();
  }

  @provide({ context: brandsContext })
  @property({ attribute: false })
  private brandsState: BrandsState = this.createBrandsState(this, [], new Set(), false);

  private createBrandsState(
    outerThis: this,
    brands: WishlistBrandListItem[],
    hiddenCodes: Set<string>,
    loaded: boolean,
  ): BrandsState {
    return new (class extends BrandsStateImpl {
      entities = brands;
      hiddenCodes = hiddenCodes;
      loaded = loaded;
      updateAll = (newState: WishlistBrandListItem[]): void => {
        outerThis.brandsState = outerThis.createBrandsState(outerThis, newState, hiddenCodes, true);
      };
      updateHiddenCodes = (codes: Set<string>): void => {
        outerThis.brandsState = outerThis.createBrandsState(outerThis, brands, codes, loaded);
      };
    })();
  }

  @provide({ context: curatedCollectionContext })
  private curatedCollectionState!: CuratedCollectionsState;

  @provide({ context: i18nContext })
  private messages!: Messages;

  @provide({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @provide({ context: collectionMenuContext })
  @property({ attribute: false })
  private collectionMenuState = this.createCollectionMenuState(this, EMPTY_COLLECTION_MENU_STATE_DATA);

  @provide({ context: productMenuContext })
  @property({ attribute: false })
  private productMenuState: ProductMenuState = this.createProductMenuState(this, EMPTY_PRODUCT_MENU_STATE_DATA);

  @provide({ context: cartContext })
  @property({ attribute: false })
  private cartState: CartState = this.createCartState(this, new Set());

  private createCartState(outerThis: this, cartProductIds: Set<string>): CartState {
    return new (class extends CartStateImpl {
      cartProductIds = cartProductIds;
      updateCartProductIds = (cartPositions: CartPositions): void => {
        const ids = cartPositions.positionen.map((p) => ProductIdentifier.create(p.syan, p.farbId).id);
        outerThis.cartState = outerThis.createCartState(outerThis, new Set(ids));
      };
    })();
  }

  @provide({ context: curatorLayerContext })
  @state()
  private curatorLayerState: CuratorLayerState = {
    open: false,
    curator: undefined,
    update: (newState) => {
      this.curatorLayerState = {
        ...this.curatorLayerState,
        ...newState,
      };
    },
  };

  @provide({ context: wishlistRouterContext })
  private router!: WishlistRouter;

  @provide({ context: vtoContext })
  private vtoService!: VtoService;

  constructor() {
    super();
    this.curatedCollectionState = new CuratedCollectionsStateImpl();
    const sharedCollection = loadFromDOM('#ents-wishlist-shared-collection');
    this.messages = loadFromDOM('#ents-wishlist-messages');
    this.productsState.updateProductsAndSharedProducts(
      loadFromDOM('#ents-wishlist-products'),
      sharedCollection?.products,
    );
    this.collectionsState.updateAll(loadFromDOM('#ents-wishlist-col'));
    this.wishlistAppConfig = loadFromDOM('#ents-wishlist-app-config');
    this.vtoService = new VtoService(this.wishlistAppConfig.vtoTtlMillis);
    this.router = this.createRouter(sharedCollection?.collection);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('ents:article:addtocart', this.updateEventItem); // addtocart dispatched by BRTN
    document.addEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);
    document.addEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.addEventListener(REMOVED_BRAND_EVENT, this.onBrandRemovedEvent);
    document.addEventListener(ADDED_BRAND_EVENT, this.onBrandAddedEvent);
    document.addEventListener('kaufen:cart:add', this.onCartAdd);

    this.productMenuState.setLoginState(this.loggedIn);

    this.setupTabNavigation();

    document.body.classList.add('ents-wishlist-ready');

    this.fetchCartData();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('ents:article:addtocart', this.updateEventItem);
    document.removeEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);
    document.removeEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.removeEventListener(REMOVED_BRAND_EVENT, this.onBrandRemovedEvent);
    document.removeEventListener(ADDED_BRAND_EVENT, this.onBrandAddedEvent);
    document.removeEventListener('kaufen:cart:add', this.onCartAdd);

    this.teardownTabNavigation();
    this.vtoService.destroy();
  }

  private setupTabNavigation() {
    const nav = document.querySelector<HTMLElement>('.ents-wishlist-nav');
    if (nav) {
      nav.addEventListener('keydown', this.handleTabKeydown);
    }
  }

  private teardownTabNavigation() {
    const nav = document.querySelector<HTMLElement>('.ents-wishlist-nav');
    if (nav) {
      nav.removeEventListener('keydown', this.handleTabKeydown);
    }
  }

  private handleTabKeydown = (e: KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight', ' '].includes(e.key)) return;

    const tabs = Array.from(document.querySelectorAll<HTMLAnchorElement>('.ents-wishlist-nav__item'));
    const currentFocused = document.activeElement;
    const currentIndex = tabs.indexOf(currentFocused as HTMLAnchorElement);
    if (currentIndex === -1) return;

    if (e.key === ' ') {
      e.preventDefault();
      tabs[currentIndex].click();
      return;
    }

    e.preventDefault();

    let nextIndex = currentIndex;
    if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    }

    const nextTab = tabs[nextIndex];
    if (nextTab) {
      nextTab.focus();
    }
  };

  private createCollectionMenuState(outerThis: this, newContext: CollectionMenuStateData): CollectionMenuState {
    return new (class extends CollectionMenuStateImpl {
      context = newContext;
      update = (newState: Partial<CollectionMenuStateData>): void => {
        outerThis.collectionMenuState = outerThis.createCollectionMenuState(outerThis, {
          ...this.context,
          ...newState,
        });
      };
    })();
  }

  private createProductMenuState(outerThis: this, newContext: ProductMenuStateData): ProductMenuState {
    return new (class extends ProductMenuStateImpl {
      context = newContext;
      update = (newState: Partial<ProductMenuStateData>): void => {
        outerThis.productMenuState = outerThis.createProductMenuState(outerThis, { ...this.context, ...newState });
      };
    })();
  }

  private createRouter(sharedCollection?: WishlistCollection) {
    if (sharedCollection) {
      return new WishlistRouter(this, [...this.createWishlistContainerRouteForSharedCollection(sharedCollection.id)]);
    }

    return new WishlistRouter(this, [
      ...this.wishlistAppConfig.favoritesEndpoints.map((favoritesEndpoint) =>
        this.createWishlistContainerRoute(favoritesEndpoint),
      ),
      ...this.createWishlistLooksRoute(),
      ...this.wishlistAppConfig.curatedCollectionEndpoints.map((curatedCollectionEndpoint) =>
        this.createWishlistCuratedCollectionRoute(curatedCollectionEndpoint),
      ),
      ...this.wishlistAppConfig.collectionEndpoints.map((collectionsEndpoint) =>
        this.createWishlistCollectionsRoute(collectionsEndpoint),
      ),
      ...(this.wishlistAppConfig.brandsEnabled
        ? this.wishlistAppConfig.brandsEndpoints.map((brandsEndpoint) => this.createWishlistBrandsRoute(brandsEndpoint))
        : []),
      ...this.createWishlistContainerRouteForCollection(),
      ...this.createWishlistContainerRouteForCuratedCollection(),
    ]);
  }

  private createWishlistContainerRoute(path: string): RouteConfig {
    return {
      path: path,
      render: () => {
        this.enterFavorites();
        return this.createWishlistContainerView();
      },
    };
  }

  private createWishlistContainerRouteForCuratedCollection(): RouteConfig[] {
    const pathWithCollectionId = `${this.wishlistAppConfig.curatedCollectionEndpoint}:collectionId/`;
    return [pathWithCollectionId, this.getPathWithoutSlash(pathWithCollectionId)].map((p) => {
      return {
        path: p,
        render: ({ collectionId }) => {
          this.enterCuratedCollections();
          if (this.loggedIn) {
            this.toggleWishlistNav(false);
            return this.renderWishlistContainer(undefined, collectionId);
          } else {
            const url = new URL(
              this.wishlistAppConfig.loginEndpointWithCuratedCollectionReferrer,
              window.location.origin,
            );
            const currentReferrer = url.searchParams.get('referrer') ?? '';
            const newReferrer = currentReferrer + collectionId + '/';
            url.searchParams.set('referrer', newReferrer);
            const finalLink = url.pathname + url.search;
            this.router.gotoWithHistory(finalLink, finalLink, true);
          }
        },
      };
    });
  }

  private createWishlistContainerRouteForCollection(): RouteConfig[] {
    const pathWithCollectionId = `${this.wishlistAppConfig.collectionEndpoint}:collectionId/`;
    return [pathWithCollectionId, this.getPathWithoutSlash(pathWithCollectionId)].map((p) => {
      return {
        path: p,
        render: ({ collectionId }) => {
          this.enterCollections();
          const wishlistCollection = this.collectionsState.find(collectionId!);
          if (!this.loggedIn || !wishlistCollection) {
            // Error messages are displayed on listview -> Redirect without reload for consistency
            // this.router.gotoWithHistory(this.wishlistAppConfig.collectionEndpoint, this.wishlistAppConfig.collectionEndpoint, true);
            return this.renderWishlistCollections(!wishlistCollection);
          } else {
            return this.createWishlistContainerView(wishlistCollection);
          }
        },
      };
    });
  }

  private createWishlistContainerRouteForSharedCollection(collectionId: string): RouteConfig[] {
    const path = `${this.wishlistAppConfig.collectionEndpoint}${collectionId}/`;
    return [path, this.getPathWithoutSlash(path)].map((p) => {
      return {
        path: p,
        render: () => {
          return this.renderWishlistContainer();
        },
      };
    });
  }

  private createWishlistCollectionsRoute(path: string): PathRouteConfig {
    return {
      path: path,
      render: () => {
        this.enterCollections();
        return this.createWishlistCollectionView(false);
      },
    };
  }

  private createWishlistCuratedCollectionRoute(path: string): PathRouteConfig {
    return {
      path: path,
      render: () => {
        this.enterCuratedCollections();
        if (this.loggedIn) {
          return this.createWishlistCuratedCollectionsView();
        } else {
          const loginLink = this.wishlistAppConfig.loginEndpointWithCuratedCollectionReferrer;
          this.router.gotoWithHistory(loginLink, loginLink, true);
        }
      },
    };
  }

  private createWishlistLooksRoute(): RouteConfig[] {
    const path = this.wishlistAppConfig.looksEndpoint;
    return [path, this.getPathWithoutSlash(path)].map((p) => ({
      path: p,
      render: () => {
        this.enterLooks();
        return nothing;
      },
    }));
  }

  private enterLooks() {
    this.setActiveTabInDom(AppMain.LOOKS_TAB);
    document.dispatchEvent(new CustomEvent('ents:wishlist:tab:changed', { detail: { tab: 'looks' } }));
    this.toggleWishlistNav(true);
    this.toggleEmptyWishlistHint(false);
    this.toggleHiddenElement('#ents-wishlist-looks-host', true);
  }

  private createWishlistBrandsRoute(path: string): PathRouteConfig {
    return {
      path: path,
      render: () => {
        this.enterBrands();
        return this.createWishlistBrandsView();
      },
    };
  }

  private enterFavorites() {
    this.toggleHiddenElement('#ents-wishlist-looks-host', false);
    this.setActiveTabInDom(AppMain.FAVORITES_TAB);
    document.dispatchEvent(new CustomEvent('ents:wishlist:tab:changed', { detail: { tab: 'favorites' } }));
  }

  private enterCollections() {
    this.toggleHiddenElement('#ents-wishlist-looks-host', false);
    this.setActiveTabInDom(AppMain.COLLECTIONS_TAB);
    document.dispatchEvent(new CustomEvent('ents:wishlist:tab:changed', { detail: { tab: 'collections' } }));
  }

  private enterCuratedCollections() {
    this.toggleHiddenElement('#ents-wishlist-looks-host', false);
    this.setActiveTabInDom(AppMain.CURATED_COLLECTION_TAB);
    document.dispatchEvent(new CustomEvent('ents:wishlist:tab:changed', { detail: { tab: 'curated-collection' } }));
  }

  private enterBrands() {
    this.setActiveTabInDom(AppMain.BRANDS_TAB);
    document.dispatchEvent(new CustomEvent('ents:wishlist:tab:changed', { detail: { tab: 'brands' } }));
  }

  private createWishlistBrandsView() {
    this.toggleWishlistNav(true);
    this.toggleEmptyWishlistHint(false);
    return html`<ents-wishlist-brands></ents-wishlist-brands>`;
  }

  private setActiveTabInDom(activeNavElementId: string) {
    const navItems = document.querySelectorAll('.ents-wishlist-nav__item');
    navItems.forEach((navItem) => {
      if (navItem.id === activeNavElementId) {
        navItem.classList.add('active');
        navItem.setAttribute('aria-selected', 'true');
        navItem.setAttribute('tabindex', '0');
      } else {
        navItem.classList.remove('active');
        navItem.setAttribute('aria-selected', 'false');
        navItem.setAttribute('tabindex', '-1');
      }
    });
  }

  private onWishlistAddedEvent = (e: WishlistMutationEvent) => {
    const productPromise = productApi.getProduct(
      this.wishlistAppConfig.productEndpoint,
      e.detail.syan.toString(),
      e.detail.articleId,
      e.detail.colorId,
    );
    productPromise.then((result) => {
      const product = e.detail.added ? { ...result, added: e.detail.added } : result;
      this.productsState.updateAll([product, ...this.productsState.entities]);
    });
  };

  private updateEventItem = (e: CustomEvent<SizeDeterminedContainer>) => {
    this.productsState.updateSelectedArticleBySyan(e.detail.syan, e.detail.articleId);
  };

  // brands are hidden rather than dropped so the snackbar Undo can restore the tile without refetching
  private onBrandRemovedEvent = (e: Event) => {
    const code = (e as BrandMutationEvent).detail.brandCode;
    if (!this.brandsState.hiddenCodes.has(code)) {
      this.brandsState.updateHiddenCodes(new Set([...this.brandsState.hiddenCodes, code]));
    }
  };

  private onBrandAddedEvent = (e: Event) => {
    const code = (e as BrandMutationEvent).detail.brandCode;
    if (this.brandsState.hiddenCodes.has(code)) {
      const next = new Set(this.brandsState.hiddenCodes);
      next.delete(code);
      this.brandsState.updateHiddenCodes(next);
    }
  };

  private onWishlistRemovedEvent = (e: WishlistMutationEvent) => {
    window.setTimeout(() => {
      const product = this.getProductByIdentifier(e.detail.identifier);
      if (product) {
        this.productsState.updateAll(this.productsState.entities.filter((p) => p !== product));
      }
    }, 400);
  };

  private getProductByIdentifier(identifier: string) {
    return this.productsState.entities.find((p) => ProductIdentifier.createByProduct(p).equalsId(identifier));
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    const productsContainer = this.shadowRoot?.querySelector('ents-wishlist-container') as WishlistContainer;
    if (changedProperties.has('productsState')) {
      if (productsContainer) {
        if (this.productsState.sharedProducts == undefined) {
          return;
        }
        if (this.productsState.sharedProducts?.length > 0) {
          this.toggleEmptySharedWishlistHint(false);
        } else {
          this.toggleEmptySharedWishlistHint(true);
        }
      }
    }
  }

  private toggleWishlistNav(show: boolean) {
    this.toggleHiddenElement('.ents-wishlist-nav', show);
  }

  private toggleEmptySharedWishlistHint(show: boolean) {
    this.toggleHiddenElement('#ents-shared-wishlist-empty-view', show);
  }

  private toggleEmptyWishlistHint(show: boolean) {
    this.toggleHiddenElement('#ents-wishlist-empty-view', show);
  }

  private toggleHiddenElement(selector: string, show: boolean) {
    const element = document.querySelector<HTMLDivElement>(selector);
    if (element) {
      if (show) {
        element.classList.remove('hidden');
      } else {
        element.classList.add('hidden');
      }
    } else if (show) {
      console.error(`failed to load ${selector} from DOM`);
    }
  }

  private createWishlistCollectionView(fromNoLongerSharedCollection: boolean) {
    if (this.productsState.entities.length === 0) {
      this.toggleEmptySharedWishlistHint(false);
    }
    return this.renderWishlistCollections(fromNoLongerSharedCollection);
  }

  private createWishlistCuratedCollectionsView() {
    this.toggleWishlistNav(true);
    this.toggleEmptyWishlistHint(false);
    return html` <ents-wishlist-curated-collections></ents-wishlist-curated-collections>`;
  }

  private createWishlistContainerView(wishlistCollection?: WishlistCollection) {
    this.toggleEmptyWishlistHint(wishlistCollection === undefined && this.productsState.entities.length === 0);
    if (this.productsState.sharedProducts) {
      this.toggleEmptySharedWishlistHint(wishlistCollection === undefined);
    }

    this.toggleWishlistNav(wishlistCollection === undefined);
    return this.renderWishlistContainer(wishlistCollection);
  }

  private renderWishlistCollections(fromNoLongerSharedCollection = false) {
    this.toggleWishlistNav(true);
    this.toggleEmptyWishlistHint(false);
    return html`
      <ents-wishlist-collections
        .loggedIn="${this.loggedIn}"
        .fromNoLongerSharedCollection="${fromNoLongerSharedCollection}"
      >
      </ents-wishlist-collections>
      <slot name="bottom-static-content-1"></slot>
      <slot name="bottom-static-content-2"></slot>
    `;
  }

  private getPathWithoutSlash(path: string): string {
    return path.endsWith('/') ? path.slice(0, -1) : path;
  }

  private onCartAdd = () => {
    window.setTimeout(() => this.fetchCartData(), 200);
  };

  private async fetchCartData() {
    try {
      const cartProducts = await cartApi.getCartProductIds(this.wishlistAppConfig.cartEndpoint);
      this.cartState.updateCartProductIds(cartProducts);
    } catch (error) {
      console.error('Failed to fetch cart data', error);
    }
  }

  private renderWishlistContainer(wishlistCollection?: WishlistCollection, curatedCollectionId?: string) {
    return html`
      <ents-wishlist-container
        class="${ifDefined(
          this.wishlistAppConfig.productTileRedesign ? 'ents-wishlist-container--redesign' : undefined,
        )}"
        .loggedIn="${this.loggedIn}"
        .wishlistCollection="${wishlistCollection}"
        .curatedCollectionId="${curatedCollectionId}"
      >
        <div slot="product-banner">
          <slot name="product-banner"></slot>
        </div>
        <div slot="gwp-banner">
          <slot name="gwp-banner"></slot>
        </div>
        <div slot="inlist-newsletter">
          <slot name="inlist-newsletter"></slot>
        </div>
      </ents-wishlist-container>
      <slot name="bottom-static-content-1"></slot>
      ${wishlistCollection || curatedCollectionId ? nothing : html` <slot name="bottom-content"></slot>`}
      <slot name="bottom-static-content-2"></slot>
    `;
  }

  render() {
    return html`
      ${this.router.outlet()}
      <slot name="looks-content"></slot>
      <ents-collection-action-menu data-test-ents-action-menu="ents-collection-action-menu">
      </ents-collection-action-menu>
      <ents-upsert-collection-layer></ents-upsert-collection-layer>
      <ents-share-collection-layer></ents-share-collection-layer>
      <ents-remove-collection-layer></ents-remove-collection-layer>
      <ents-remove-article-layer></ents-remove-article-layer>
      <ents-curator-layer></ents-curator-layer>
    `;
  }
}
